<template>
    <div id="login-wrapper" :class="['h-screen', 'relative']">
        <TransferDialog @on-confirm="handleConfirm" />
        <TheHeading title="" :leftArrow="action !== 'login'" @on-back="onBack" />
        <Logo :title="h5Title" />
        <div class="login-container">
            <component
                :is="formComponentKey"
                :action="action"
                :captchaText="captchaText"
                :captchaDisabled="captchaDisabled"
                @on-action="handleAction"
                @on-start="start"
                @on-end="end"
                @on-submit="handleSubmit"
            />
        </div>
        <OAuth v-show="oauthVisible" />
        <div class="agreed-checkout absolute flex">
            <van-checkbox v-model="agreed" />
            <i18n path="login.agreed" tag="div" class="content">
                <a
                    place="privacy"
                    @click="
                        openProtocol(protocol.privacyAgreementUrl, $t('webview.privacyAgreement'))
                    "
                >
                    {{ $t('login.privacyAgreement') }}
                </a>
                <a
                    place="service"
                    @click="
                        openProtocol(
                            protocol.userServiceAgreementUrl,
                            $t('webview.serviceAgreement')
                        )
                    "
                >
                    {{ $t('login.serviceAgreement') }}
                </a>
            </i18n>
        </div>
    </div>
</template>

<script>
/* eslint-disable camelcase */
import { mapState } from 'vuex';
import Cookies from 'js-cookie';
import Logs from '@/lib/logs';
import { jsonParser } from '@/lib/utils';
import useEmail from '@/mixins/useEmail';
import useTransfer from '@/mixins/useTransfer';
import { Platform, EmailType } from '@/enums';
import { getProtocol } from '@/api/login';
import { Action } from '@/views/Login/constant';
import Logo from '@/views/Login/components/Logo.vue';
import LoginForm from '@/views/Login/components/LoginForm.vue';
import RegisterForm from '@/views/Login/components/RegisterForm.vue';
import OAuth from '@/views/Login/components/OAuth.vue';
import TransferDialog from './TransferDialog.vue';

export default {
    name: 'Login',
    mixins: [useEmail, useTransfer],
    components: {
        Logo,
        LoginForm,
        RegisterForm,
        OAuth,
        TransferDialog,
    },
    data() {
        return {
            action: Action.Login,
            Action,
            agreed: true, // 默认同意协议
            protocol: {
                privacyAgreementUrl: '',
                userServiceAgreementUrl: '',
            },
            accountInfo: {}, // Tencent LI 账号信息
            logs: { web_pageid: 'ghome-web-dn2-login' },
        };
    },
    computed: {
        ...mapState({
            // eslint-disable-next-line object-curly-newline
            platform: state => state.platform || jsonParser(sessionStorage.getItem('platform')),
        }),

        oauthVisible() {
            return this.action === Action.Login;
        },
        h5Title() {
            switch (this.action) {
                case Action.Login:
                    return this.$t('login.LoginByEmail');
                case Action.Register:
                    return this.$t('login.registerByEmail');
                case Action.ResetPassword:
                    return this.$t('login.resetEmailPassword');
                default:
                    return '';
            }
        },
        formComponentKey() {
            return this.action === Action.Login ? 'LoginForm' : 'RegisterForm';
        },
    },
    created() {
        const { action = Action.Login } = this.$route.query;
        sessionStorage.setItem('emailType', JSON.stringify(action));
        this.action = [EmailType.StaticLogin, EmailType.CodeLogin].includes(action)
            ? Action.Login
            : action;

        // 协议政策
        getProtocol().then(data => {
            Object.assign(this.protocol, data);
        });

        Logs.imgLog_PageLoad(this.logs);
    },
    methods: {
        handleConfirm(form) {
            this.accountInfo = form;
        },

        onBack() {
            this.action = Action.Login;
            this.end();
        },

        handleAction(action) {
            this.action = action;
            this.end();
        },

        async handleSubmit(emailType, form) {
            // 校验是否同意协议
            if (!this.agreed) {
                return this.$toast(this.$t('login.agreePlaceholder'));
            }

            // 校验是否获取验证码（防止用户乱填）
            if (form.captcha && !this.flowIdList[emailType]) {
                return this.$toast(this.$t('login.obtainCaptcha'));
            }

            const { email } = form;
            switch (emailType) {
                case EmailType.StaticLogin: {
                    const cipherInfo = await this.ueInitClient(email, emailType);
                    await this.emailStaticLogin(
                        {
                            email: cipherInfo,
                            password: form.password,
                        },
                        () => {
                            Logs.imgLog_Button({
                                ...this.logs,
                                web_buttonid: 'static-login',
                                web_userid: `cookie=${Cookies.get('WEB_TOKEN')}`,
                            });
                            this.transferAccount(this.accountInfo);
                        }
                    );
                    break;
                }
                case EmailType.CodeLogin:
                    this.emailCaptchaLogin(form).then(() => {
                        Logs.imgLog_Button({
                            ...this.logs,
                            web_buttonid: 'code-login',
                            web_userid: `cookie=${Cookies.get('WEB_TOKEN')}`,
                        });
                        this.transferAccount(this.accountInfo);
                    });
                    break;
                case Action.Register:
                    this.emailRegister(form).then(() => {
                        Logs.imgLog_Button({
                            ...this.logs,
                            web_buttonid: 'register',
                            web_userid: `cookie=${Cookies.get('WEB_TOKEN')}`,
                        });
                        this.transferAccount(this.accountInfo);
                    });
                    break;
                case Action.ResetPassword:
                    this.emailResetPassword(form).then(() => {
                        Logs.imgLog_Button({
                            ...this.logs,
                            web_buttonid: 'reset-password',
                            web_userid: `cookie=${Cookies.get('WEB_TOKEN')}`,
                        });
                        this.transferAccount(this.accountInfo);
                    });
                    break;
                default:
                    break;
            }
        },

        openProtocol(src, name) {
            Logs.imgLog_Button({
                ...this.logs,
                web_buttonid: 'protocol',
                web_ext_json: `protocol_type=${name}`,
            });

            if (this.platform === Platform.WebViewTransfer) {
                this.$router.push({
                    name: 'Protocol',
                    query: { src, name, showNavbar: true },
                });
            } else {
                window.open(src, '_blank');
            }
        },
    },
};
</script>

<style scoped lang="scss">
#login-wrapper {
    min-height: 690px;
}

/deep/ .van-overlay {
    position: absolute;
}

.nav-bar {
    margin-bottom: 20px;
}

.login-container {
    padding: 0 30px;
    overflow: hidden;
}
.webview .login-container {
    padding-top: 20px;
}

/deep/ .submit-btn {
    border-radius: 8px;
}

.agreed-checkout {
    left: 0;
    bottom: 0;
    padding: 0 16px;
    margin-bottom: 20px;
    background-color: transparent !important;
    border-radius: unset !important;
    width: unset;
    color: #999;

    div.van-checkbox {
        width: 35px;
    }

    /deep/.van-checkbox__icon--checked .van-icon {
        background-color: #e4004f;
        border-color: #e4004f;
    }

    div.content {
        font-size: 14px;
    }
}
</style>
